import "@/App.css";
import { Capacitor } from "@capacitor/core";
import { ClerkProvider, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { toast } from "sonner";

import AdSenseRouteScript from "@/components/AdSenseRouteScript";
import AnalyticsIdentity from "@/components/AnalyticsIdentity";
import PreferencesSync from "@/components/PreferencesSync";
import { Toaster } from "@/components/ui/sonner";
import i18n from "@/i18n";
import {
  clearAnonymousId,
  clearAnonymousTrialLock,
  hasAnonymousId,
  markAnonymousTrialLocked,
} from "@/lib/demo";
import { signupWallTarget } from "@/lib/limits";
import LandingPage from "@/pages/LandingPage";

const AboutPage = lazy(() => import("@/pages/AboutPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const BookUpload = lazy(() => import("@/pages/BookUpload"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DisclaimerPage = lazy(() => import("@/pages/DisclaimerPage"));
const FlashcardSession = lazy(() => import("@/pages/FlashcardSession"));
const GuideArticlePage = lazy(() => import("@/pages/GuideArticlePage"));
const GuidesPage = lazy(() => import("@/pages/GuidesPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const Progress = lazy(() => import("@/pages/Progress"));
const QuizSession = lazy(() => import("@/pages/QuizSession"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const SharedQuiz = lazy(() => import("@/pages/SharedQuiz"));
const LearnHubPage = lazy(() => import("@/pages/LearnHubPage"));
const TopicPage = lazy(() => import("@/pages/TopicPage"));
const AlternativesHubPage = lazy(() => import("@/pages/AlternativesHubPage"));
const AlternativePage = lazy(() => import("@/pages/AlternativePage"));
const StudyCoachHubPage = lazy(() => import("@/pages/StudyCoachHubPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const UseCasePage = lazy(() => import("@/pages/UseCasePage"));

const appShellStyle = {
  paddingTop: "env(safe-area-inset-top)",
  paddingBottom: "env(safe-area-inset-bottom)",
  paddingLeft: "env(safe-area-inset-left)",
  paddingRight: "env(safe-area-inset-right)",
};

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const isClerkConfigured = clerkPubKey && !clerkPubKey.includes("YOUR_CLERK");
let apiModulePromise;

const isSignupRequiredCode = (error) => error?.response?.data?.error?.code === "SIGNUP_REQUIRED";

const CLAIM_TIMEOUT_MS = 4000;

async function getApiModule() {
  if (!apiModulePromise) {
    apiModulePromise = import("@/lib/api");
  }
  return apiModulePromise;
}

function RouteLoading() {
  return <div className="min-h-screen bg-app" />;
}

function DeferredRoute({ children }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

function ClerkRootRoute() {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    // Render the public landing content immediately so first paint isn't blocked
    // on Clerk hydration for signed-out visitors.
    return <LandingPage />;
  }

  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

function AnonymousTrialRoute({ children }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;

    const checkTrial = async () => {
      try {
        const { api } = await getApiModule();
        const response = await api.getTrialStatus();
        if (!active) return;

        if (response.data.sign_up_required) {
          markAnonymousTrialLocked();
          setState("blocked");
          return;
        }

        clearAnonymousTrialLock();
        setState("allowed");
      } catch (error) {
        if (!active) return;

        if (isSignupRequiredCode(error)) {
          markAnonymousTrialLocked();
          setState("blocked");
          return;
        }

        clearAnonymousTrialLock();
        setState("allowed");
      }
    };

    checkTrial();
    return () => {
      active = false;
    };
  }, [location.pathname]);

  if (state === "loading") {
    return <RouteLoading />;
  }

  if (state === "blocked") {
    const target = isClerkConfigured ? signupWallTarget(navigationType) : "/";
    return (
      <Navigate
        to={target}
        replace
        state={target === "/sign-up" ? { trialLocked: true } : undefined}
      />
    );
  }

  return children;
}

function AuthenticatedRoute({ children }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;

    if (!isLoaded) return undefined;

    const initAuth = async () => {
      const { api, setAuthTokenProvider } = await getApiModule();

      if (!active) return;

      if (!isSignedIn) {
        setAuthTokenProvider(null);
        setReady(true);
        return;
      }

      setAuthTokenProvider(() => getToken());

      // Adopt whatever this browser did anonymously before signing up, so the
      // dashboard's first render already shows that progress. Cleared before the
      // await, not after: this effect re-runs on every getToken identity change,
      // which right after sign-in is a handful of times.
      if (hasAnonymousId()) {
        clearAnonymousId();
        // Bounded, because the shell must never be hostage to this: the request
        // sits behind getToken(), which Clerk can leave pending while it hydrates,
        // and a blank screen right after signup is worse than a late claim. On
        // timeout the claim still lands server-side, one navigation later.
        await Promise.race([
          api.claimAnonymousData().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, CLAIM_TIMEOUT_MS)),
        ]);
      }

      setReady(true);

      let pendingShareId = null;
      try {
        pendingShareId = sessionStorage.getItem("pendingReferralShareId");
      } catch {
        // storage unavailable
      }
      if (!pendingShareId) return;
      try {
        sessionStorage.removeItem("pendingReferralShareId");
      } catch {
        // storage unavailable
      }
      try {
        const { data } = await api.claimReferral(pendingShareId);
        if (active && data?.rewarded) {
          toast.success(i18n.t("referral.rewarded"));
        }
      } catch {
        // Claim can fail (already claimed, not eligible, etc.) — stay silent.
      }
    };
    initAuth();

    return () => {
      active = false;
    };
  }, [getToken, isLoaded, isSignedIn]);

  if (!ready) {
    return <RouteLoading />;
  }

  return children;
}

function ProtectedRoute({ children }) {
  return (
    <>
      <SignedIn>
        <AuthenticatedRoute>{children}</AuthenticatedRoute>
      </SignedIn>
      <SignedOut>
        <AnonymousTrialRoute>{children}</AnonymousTrialRoute>
      </SignedOut>
    </>
  );
}

function SignedInOnlyRoute({ children }) {
  return (
    <>
      <SignedIn>
        <AuthenticatedRoute>{children}</AuthenticatedRoute>
      </SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
    </>
  );
}

function AppContent() {
  if (!isClerkConfigured) {
    // Demo mode without Clerk
    return (
      <BrowserRouter>
        <AdSenseRouteScript />
        <div className="min-h-screen bg-app" style={appShellStyle}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/guides"
              element={
                <DeferredRoute>
                  <GuidesPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/guides/:slug"
              element={
                <DeferredRoute>
                  <GuideArticlePage />
                </DeferredRoute>
              }
            />
            <Route
              path="/study-coach"
              element={
                <DeferredRoute>
                  <StudyCoachHubPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/study-coach/:slug"
              element={
                <DeferredRoute>
                  <UseCasePage />
                </DeferredRoute>
              }
            />
            <Route
              path="/alternatives"
              element={
                <DeferredRoute>
                  <AlternativesHubPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/alternatives/:slug"
              element={
                <DeferredRoute>
                  <AlternativePage />
                </DeferredRoute>
              }
            />
            <Route
              path="/about"
              element={
                <DeferredRoute>
                  <AboutPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/contact"
              element={
                <DeferredRoute>
                  <ContactPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <AnonymousTrialRoute>
                  <DeferredRoute>
                    <Dashboard />
                  </DeferredRoute>
                </AnonymousTrialRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <AnonymousTrialRoute>
                  <DeferredRoute>
                    <BookUpload />
                  </DeferredRoute>
                </AnonymousTrialRoute>
              }
            />
            <Route
              path="/learn"
              element={
                <DeferredRoute>
                  <LearnHubPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/learn/:slug"
              element={
                <DeferredRoute>
                  <TopicPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/quiz/:bookId"
              element={
                <AnonymousTrialRoute>
                  <DeferredRoute>
                    <QuizSession />
                  </DeferredRoute>
                </AnonymousTrialRoute>
              }
            />
            <Route
              path="/flashcards/:bookId"
              element={
                <AnonymousTrialRoute>
                  <DeferredRoute>
                    <FlashcardSession />
                  </DeferredRoute>
                </AnonymousTrialRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <AnonymousTrialRoute>
                  <DeferredRoute>
                    <Progress />
                  </DeferredRoute>
                </AnonymousTrialRoute>
              }
            />
            <Route
              path="/pricing"
              element={
                <DeferredRoute>
                  <PricingPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/disclaimer"
              element={
                <DeferredRoute>
                  <DisclaimerPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/terms"
              element={
                <DeferredRoute>
                  <TermsPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={
                <DeferredRoute>
                  <TermsPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <DeferredRoute>
                  <PrivacyPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <DeferredRoute>
                  <PrivacyPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/refund"
              element={
                <DeferredRoute>
                  <RefundPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/refund-policy"
              element={
                <DeferredRoute>
                  <RefundPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <DeferredRoute>
                  <SettingsPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/shared/:shareId"
              element={
                <DeferredRoute>
                  <SharedQuiz />
                </DeferredRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    );
  }

  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <PreferencesSync />
      <AnalyticsIdentity />
      <BrowserRouter>
        <AdSenseRouteScript />
        <div className="min-h-screen bg-app" style={appShellStyle}>
          <Routes>
            <Route path="/" element={<ClerkRootRoute />} />
            <Route
              path="/guides"
              element={
                <DeferredRoute>
                  <GuidesPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/guides/:slug"
              element={
                <DeferredRoute>
                  <GuideArticlePage />
                </DeferredRoute>
              }
            />
            <Route
              path="/study-coach"
              element={
                <DeferredRoute>
                  <StudyCoachHubPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/study-coach/:slug"
              element={
                <DeferredRoute>
                  <UseCasePage />
                </DeferredRoute>
              }
            />
            <Route
              path="/alternatives"
              element={
                <DeferredRoute>
                  <AlternativesHubPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/alternatives/:slug"
              element={
                <DeferredRoute>
                  <AlternativePage />
                </DeferredRoute>
              }
            />
            <Route
              path="/about"
              element={
                <DeferredRoute>
                  <AboutPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/contact"
              element={
                <DeferredRoute>
                  <ContactPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/sign-in/*"
              element={
                <DeferredRoute>
                  <AuthPage type="sign-in" />
                </DeferredRoute>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <DeferredRoute>
                  <AuthPage type="sign-up" />
                </DeferredRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DeferredRoute>
                    <Dashboard />
                  </DeferredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <DeferredRoute>
                    <BookUpload />
                  </DeferredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learn"
              element={
                <DeferredRoute>
                  <LearnHubPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/learn/:slug"
              element={
                <DeferredRoute>
                  <TopicPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/quiz/:bookId"
              element={
                <ProtectedRoute>
                  <DeferredRoute>
                    <QuizSession />
                  </DeferredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flashcards/:bookId"
              element={
                <ProtectedRoute>
                  <DeferredRoute>
                    <FlashcardSession />
                  </DeferredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <DeferredRoute>
                    <Progress />
                  </DeferredRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing"
              element={
                <DeferredRoute>
                  <PricingPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/disclaimer"
              element={
                <DeferredRoute>
                  <DisclaimerPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/terms"
              element={
                <DeferredRoute>
                  <TermsPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={
                <DeferredRoute>
                  <TermsPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <DeferredRoute>
                  <PrivacyPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <DeferredRoute>
                  <PrivacyPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/refund"
              element={
                <DeferredRoute>
                  <RefundPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/refund-policy"
              element={
                <DeferredRoute>
                  <RefundPolicyPage />
                </DeferredRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <SignedInOnlyRoute>
                  <DeferredRoute>
                    <SettingsPage />
                  </DeferredRoute>
                </SignedInOnlyRoute>
              }
            />
            <Route
              path="/shared/:shareId"
              element={
                <DeferredRoute>
                  <SharedQuiz />
                </DeferredRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </ClerkProvider>
  );
}

function App() {
  useEffect(() => {
    const isNativePlatform = Capacitor.isNativePlatform();

    if (
      !isNativePlatform &&
      "serviceWorker" in navigator &&
      process.env.NODE_ENV === "production"
    ) {
      navigator.serviceWorker.register("/sw.js").catch((error) => {
        console.warn("Service worker registration skipped:", error);
      });
    }

    if (!isNativePlatform) return;

    const setupNativeUi = async () => {
      try {
        const [{ Keyboard }, { SplashScreen }, { StatusBar, Style }] = await Promise.all([
          import("@capacitor/keyboard"),
          import("@capacitor/splash-screen"),
          import("@capacitor/status-bar"),
        ]);
        await StatusBar.show();
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: "#FDFBF7" });
        await StatusBar.setStyle({ style: Style.Light });
        await SplashScreen.hide();
        await Keyboard.setAccessoryBarVisible({ isVisible: true });
      } catch (error) {
        console.warn("Native UI setup skipped:", error);
      }
    };

    setupNativeUi();
  }, []);

  return <AppContent />;
}

export default App;
